import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingBag, Trash2 } from 'lucide-react';
import { useWishlist } from '../context/WishlistContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';

export default function WishlistPage() {
  const { wishlist, loading, removeFromWishlist } = useWishlist();
  const { addToCart, setIsOpen } = useCart();
  const { user, login } = useAuth();

  const handleAddToCart = async (product) => {
    if (product.variants && product.variants.length > 0) {
      // For simplicity, add first available size
      const availableVariant = product.variants.find(v => v.stock > 0);
      if (availableVariant) {
        const success = await addToCart(product.product_id, availableVariant.size, 1);
        if (success) {
          toast.success('Ajouté au panier');
          setIsOpen(true);
        }
      } else {
        toast.error('Produit en rupture de stock');
      }
    }
  };

  const handleRemove = async (productId) => {
    await removeFromWishlist(productId);
    toast.success('Retiré de la wishlist');
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center">
        <div className="text-center">
          <Heart size={48} className="text-[#E0DFDD] mx-auto mb-4" />
          <h1 className="font-serif text-2xl mb-4">Ma Wishlist</h1>
          <p className="text-[#6B6A68] mb-6">Connecte-toi pour voir ta wishlist</p>
          <button 
            onClick={login}
            className="btn-primary px-6 py-3 text-sm font-medium"
            data-testid="login-btn"
          >
            Se connecter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FCFBF9]" data-testid="wishlist-page">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <nav className="breadcrumb flex items-center gap-2 text-sm">
          <Link to="/" className="hover:text-[#1A1514]">Accueil</Link>
          <span>/</span>
          <span className="text-[#1A1514]">Ma Wishlist</span>
        </nav>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-serif text-3xl mb-8">Ma Wishlist ({wishlist.length})</h1>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-product bg-[#F5F4F2] mb-4" />
                <div className="h-4 bg-[#F5F4F2] w-3/4 mb-2" />
                <div className="h-4 bg-[#F5F4F2] w-1/2" />
              </div>
            ))}
          </div>
        ) : wishlist.length === 0 ? (
          <div className="text-center py-16">
            <Heart size={48} className="text-[#E0DFDD] mx-auto mb-4" />
            <p className="text-[#6B6A68] mb-6">Ta wishlist est vide</p>
            <Link to="/shop" className="btn-primary px-6 py-3 inline-block text-sm font-medium">
              Découvrir la Collection
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {wishlist.map((item) => (
              <div key={item.wishlist_id} className="group" data-testid={`wishlist-item-${item.product_id}`}>
                <Link to={`/product/${item.product_id}`}>
                  <div className="aspect-product bg-[#F5F4F2] overflow-hidden relative mb-4">
                    {item.product?.images?.[0]?.url ? (
                      <img
                        src={item.product.images[0].url}
                        alt={item.product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="font-serif text-2xl text-[#E0DFDD]">Bloom</span>
                      </div>
                    )}
                  </div>
                </Link>
                
                <h3 className="font-serif text-lg mb-1">
                  <Link to={`/product/${item.product_id}`} className="hover:text-[#D4AF37] transition-colors">
                    {item.product?.name}
                  </Link>
                </h3>
                
                <p className="text-sm mb-4">
                  {item.product?.sale_price ? (
                    <>
                      <span className="text-[#C87979]">{formatPrice(item.product.sale_price)}</span>
                      <span className="text-[#6B6A68] line-through ml-2">{formatPrice(item.product.price)}</span>
                    </>
                  ) : (
                    <span>{formatPrice(item.product?.price)}</span>
                  )}
                </p>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleAddToCart(item.product)}
                    className="flex-1 btn-primary py-2 text-xs font-medium flex items-center justify-center gap-2"
                    data-testid={`add-to-cart-${item.product_id}`}
                  >
                    <ShoppingBag size={14} />
                    Ajouter
                  </button>
                  <button
                    onClick={() => handleRemove(item.product_id)}
                    className="w-10 border border-[#E0DFDD] hover:border-[#C87979] hover:text-[#C87979] flex items-center justify-center transition-colors"
                    data-testid={`remove-wishlist-${item.product_id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
